<template>
  <div class="checkin">
    <checkin-stepper class='checkin-stepper'/>
    <v-container class="checkin">
      <v-card>
        <v-card-text align="center">
          <h2 class='py-3'>
            {{ $t('order.payment.products_list') }}
          </h2>

          <v-progress-circular
            v-if="this.loading_products || (!this.loading_products &&  !this.completedCheckinSteps)"
            size='50'
            color='primary'
            indeterminate
            class='mb-3'
          />

          <v-alert
            v-else-if="!!notice"
            outlined
            type='warning'
            max-width='65%'
            class='d-inline-block'
          >
            {{ notice }}
          </v-alert>

          <div v-else-if="totalAmount && totalAmount > 0">
            <v-data-table id="payment_summary"
                          :headers="headers"
                          hide-default-footer
                          mobile-breakpoint="0"
            >
              <template v-slot:body="{}">
                <tbody>
                <tr v-for="product in products" :key="product.id">
                  <td>{{ product.name }}</td>
                  <td>{{ product.qty }} x {{ italianFormat(product.total_amount) }} €</td>
                </tr>
                </tbody>
                <tfoot>
                <tr>
                  <td>{{ $t("order.payment.total") }}</td>
                  <td>{{ italianFormat(totalAmount) }} €</td>
                </tr>
                </tfoot>
              </template>
            </v-data-table>
            <stripe-card-element
              id="stripe_container"
              :stripe_publishable_key='stripe_public_key'
              :stripe_account='stripe_account'
              :already_paid='productsPaid'
              :uuid='order.uuid'
              :pay_url='pay_url'
              @update-paid-state='updatePaidState'
            />
          </div>

          <div v-else>
            {{ $t("order.payment.nothing_to_pay") }}
          </div>

        </v-card-text>
      </v-card>

      <div
        v-if="!loading_products && totalAmount && totalAmount > 0 && !productsPaid"
        class="text-center text-caption my-5"
      >
        {{ $t("order.payment.payment_hint") }} <br>
        {{ mandatoryStep ? $t("order.payment.mandatory_payment_hint") : $t("order.payment.optional_payment_hint") }}
      </div>

    </v-container>
    <checkin-bottom-navigation>
      <template v-slot:prev-button>
        <v-btn @click="goToPrevStepper">
          {{ $t("commons.prev") }}
        </v-btn>
      </template>
      <template v-slot:next-button>
        <v-btn v-if = "!isTheEnd"
          @click="goToNextStepper"
          color="primary"
          :disabled="nextStepperDisabled"
          :loading='submitting'
        >
          {{ $t("commons.next") }}
        </v-btn>
        <v-btn
          v-else-if="formAndMandatoryStepsCompleted"
          @click="completeCheckin()"
          color="success"
          :disabled="nextStepperDisabled"
          :loading='submitting'
        >
          {{ $t("commons.end") }}
        </v-btn>
        <v-btn
          v-else
          @click="completeCheckin()"
          :disabled="nextStepperDisabled"
          color="primary"
          :loading='submitting'
        >
          {{ $t("commons.close_checkin") }}
        </v-btn>
      </template>
    </checkin-bottom-navigation>
  </div>

</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import CheckinStepper from '../../checkin/checkin_stepper'
  import CheckinBottomNavigation from '../../checkin/checkin_bottom_navigation'
  import StripeCardElement from '../../checkin/stripe_card_element'
  import { orderFetchMixin } from '../../../mixins/order_fetch_mixin'
  import { orderMethodsMixin } from '../../../mixins/order_methods_mixin'

export default {
  name: "CheckinPayment",

  components: {
    CheckinStepper,
    CheckinBottomNavigation,
    StripeCardElement
  },

  mixins: [
    orderFetchMixin,
    orderMethodsMixin
  ],

  async created() {
    await this.fetchOrder(this.currentOrderId)

    this.$store
      .dispatch('order/fetchProducts', { orderId: this.currentOrderId, reservationId: this.currentReservationId, language: this.$i18n.locale })
      .then(response => {
        this.loading_products = false
        this.products = response.products
        this.totalAmount = response.total_amount
        this.notice = response.notice
        this.release_mandatory_constraint = response.release_mandatory_constraint === true
        this.stripe_account = response.stripe_account
        this.stripe_public_key = response.stripe_public_key
        this.pay_url = response.pay_url
      }).catch(error => {
        this.$router.push({ name: 'dashboard' })
        this.$store.dispatch('layout/replaceAlert', {
          type: 'error',
          key: 'generic_api_error'
        })
      })
  },

  data() {
    return {
      products: null,
      totalAmount: null,
      notice: null,
      release_mandatory_constraint: false,
      stripe_account: null,
      stripe_public_key: null,
      pay_url: null,
      productsPaid : false,
      loading_products : true
    }
  },
  watch:{
    completedCheckinSteps(newValue, oldValue){
      let paymentStep = newValue[this.currentStep-1]
      if(this.productsPaid != paymentStep.complete){
        this.productsPaid = paymentStep.complete
      }
    },
  },
  computed: {
    ...mapState('layout', ['loading', 'currentStep', 'submitting', 'completedCheckinSteps']),
    ...mapState('order', ['order']),
    headers() {
      return  [
        {text: this.$t('order.payment.product'), value:"product", sortable:false},
        {text: this.$t('order.payment.import'), value:"import", sortable:false}
      ]
    },
    formAndMandatoryStepsCompleted() {
      return this.completedCheckinSteps && this.completedCheckinSteps.every((step) => step.completed )
    },
    presenceRemovableSteps() {
      return this.completedCheckinSteps.some(step => ['contract'].includes(step.name))
    },
    isTheEnd() {
      return !!this.reservation && !this.presenceRemovableSteps
    },
    mandatoryStep(){
      let paymentStep = this.completedCheckinSteps[this.currentStep-1]
      return paymentStep ? paymentStep.mandatory : false
    },
    nextStepperDisabled() {
      return this.mandatoryStep &&
        !this.release_mandatory_constraint &&
        !this.productsPaid &&
        (!!this.notice || (this.totalAmount !== undefined && this.totalAmount !== null && this.totalAmount > 0))
    }
  },
  methods: {
    ...mapActions('layout', ['goToPrevStepper', 'goToNextStepper']),
    updatePaidState(result){
      this.productsPaid = result
    },
    italianFormat(n){
      return Number(n).toLocaleString('it-IT', {minimumFractionDigits: 2})
    }
  }
}
</script>

<style>
  h2{
    text-align: center;
    margin-bottom: 10px;
  }
  #stripe_container.hide{
    display: none
  }
  #payment_summary table{
    border: 1px solid rgba(0, 0, 0, .12);
  }
  #payment_summary tfoot td{
    border-top: 1px solid rgba(0, 0, 0, .12);
  }

  #payment_summary thead th{
    font-size: .875rem;
  }

  #payment_summary thead th,
  #payment_summary tfoot td{
    font-weight: bold;
    background-color: #eee;
    color: #000;
  }
  #payment_summary thead th:first-child,
  #payment_summary td:first-child{
    border-right: 1px solid rgba(0, 0, 0, .12);
  }
</style>
